import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';

import localIdMap from 'common/upload/localIdMap';
import { UploadedFileAsset, MediaType, getFileFormat } from 'common/upload/Upload';
import { MediaPathsToAltText, SelectedSource } from 'social/publisher/Publisher.types';

import 'common/gallery/gallery-preview/GalleryPreview.scss';

export interface OnDeleteFunc {
    (index: number, allFiles?: (File | UploadedFileAsset)[]): void;
}

export interface OnMediaClickFunc {
    (index: number): void;
}

export interface Props {
    galleryData: (File | UploadedFileAsset)[];
    /** index to be used to high-light selected media */
    activeIndex?: number;
    /** ability to view media as preview, default: true, pass false to view media as list */
    displayPreview?: boolean;
    /** default: 95px */
    height?: any;
    /** default: 125px */
    width?: any;
    /** when true: display overflowing media on multiple rows, false: only single row, default: true */
    wrapContent?: boolean;
    /** enable/disable overflow when single row, default: false */
    overflowXContent?: boolean;
    /** enable/disable media deletion, will display delete-icon on hover, default: true */
    canDelete?: boolean;
    /** display file name on hover */
    displayFileNameOnHover?: boolean;
    /** enable/disable opening of modal on entire media click, default: false */
    openModalOnMediaClick?: boolean;
    /** enable/disable opening of modal on icon click, default :true */
    openModalOnHandleIconClick?: boolean;
    /** maximum number of images to be displayed, rest will be collapsed, defauls to null (no limit) */
    maxCount?: number | null;
    /** highlight selected media */
    highlightSelected?: boolean;
    /** display controls for media type: video */
    showVideoControls?: boolean;
    /** display controls for media type: audio */
    showAudioControls?: boolean;
    /** enable/disable drag-n-drop ordering of media, default: false */
    canReOrder?: boolean;
    canEditImage?: boolean;
    /** if media is overflowing, pass true to scroll selected media into view, default: false */
    scrollSelectedMediaInToView?: boolean;
    /** will be called when media is deleted */
    onDelete?: OnDeleteFunc;
    /** will be called when media is clicked */
    onMediaClick?: OnMediaClickFunc;
    /** cb when media is re-ordered */
    onMediaReOrder?: any;
    onMediaEditClick?: any;
    onMediaEdited?: any;
    onMediaAltTextClick?: any;
    mediaPathsToAltText?: MediaPathsToAltText;
    selectedSources?: SelectedSource;
}

export const GalleryPreview: FunctionComponent<Props> = ({
    galleryData,
    activeIndex,
    displayPreview,
    height,
    width,
    wrapContent,
    overflowXContent,
    canDelete,
    displayFileNameOnHover,
    openModalOnMediaClick,
    openModalOnHandleIconClick,
    maxCount,
    highlightSelected,
    showVideoControls,
    showAudioControls,
    canReOrder,
    canEditImage,
    scrollSelectedMediaInToView,
    onDelete,
    onMediaClick,
    onMediaReOrder,
    onMediaEditClick,
    onMediaAltTextClick,
    mediaPathsToAltText,
    selectedSources,
}: Props): JSX.Element => {
    const { t } = useTranslation(['common/gallery']);
    const [mediaData, setMediaData] = useState(galleryData);
    const [currentIndex, setCurrentIndex] = useState(activeIndex);
    const renderIdsRef = useRef<string[]>([]);
    const containerRef = useRef(null);

    useEffect(() => {
        setCurrentIndex(activeIndex);
        if (scrollSelectedMediaInToView && containerRef) {
            const node: any = containerRef.current;

            if (node && node.children && node.children[activeIndex!]) {
                node.children[activeIndex!].scrollIntoView({
                    block: 'center',
                    behavior: 'auto',
                });
            }
        }
    }, [activeIndex, scrollSelectedMediaInToView]);

    useEffect(() => {
        // Maintain a stable render id per incoming item index to guarantee unique React keys
        setMediaData(galleryData);
        {
            const prev = renderIdsRef.current || [];
            const next: string[] = [];

            for (let i = 0; i < (galleryData ? galleryData.length : 0); i++) {
                next[i] = prev[i] || `render-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`;
            }
            renderIdsRef.current = next;
        }
    }, [galleryData]);

    const reOrder = (list: any, startIndex: any, endIndex: any): any => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);

        result.splice(endIndex, 0, removed);
        return result;
    };

    const handleDragEnd = ({ source, destination }: any): void => {
        if (!destination) {
            return;
        }
        const items: any = reOrder(mediaData, source.index, destination.index);

        setMediaData(items);
        if (onMediaReOrder) {
            onMediaReOrder(items);
        }
    };

    const HandlerIcon = ({
        className,
        mediaIndex,
        handleClick,
        iconName,
        tooltip,
    }: {
        className: string;
        mediaIndex: number;
        handleClick: any;
        iconName: string;
        tooltip: string;
    }): JSX.Element => (
        <OverlayTrigger overlay={<Tooltip id={`handler-tooltip-${className}`}>{t(tooltip)}</Tooltip>}>
            <span
                className={`handler-icon ${className}`}
                role="button"
                onClick={(): void => {
                    if (handleClick) {
                        handleClick(mediaIndex);
                    }
                }}
                onKeyPress={(): void => {
                    // Do Nothing
                }}
                tabIndex={0}
            >
                <i className={iconName} />
            </span>
        </OverlayTrigger>
    );

    const renderMediaPreview = (media: any, index: number): JSX.Element | null => {
        let renderer: any = null;
        let showPseudoHover = true;
        let { url } = media;

        if (!url && media instanceof File) {
            url = URL.createObjectURL(media);
        }
        const mediaFormat = getFileFormat(media.type);

        switch (mediaFormat) {
            case MediaType.Image:
                renderer = <img className={canEditImage ? 'response-editor-img' : ''} src={url} alt={media.name} />;
                break;
            case MediaType.Video:
                renderer = <ReactPlayer height="100%" width="100%" controls={showVideoControls} url={url} />;
                showPseudoHover = false;
                break;
            case MediaType.Audio:
                renderer = (
                    <audio controls={showAudioControls} src={url}>
                        <track kind="captions" />
                    </audio>
                );
                showPseudoHover = false;
                break;
            case MediaType.Text:
                renderer = (
                    <>
                        <i className="icon-file-pdf media-icon" />
                    </>
                );
                break;
            default:
                renderer = null;
                break;
        }

        const mediaKey = media
            ? media.id ||
              media.url ||
              localIdMap.get(media) ||
              (media as any).localId ||
              `${media.name || 'media'}-${index}`
            : `media-${index}`;

        const renderId = renderIdsRef.current[index] || `${mediaKey}-${index}`;

        return (
            <Draggable key={renderId} draggableId={renderId} index={index} isDragDisabled={!canReOrder!}>
                {(provided, snapshot): JSX.Element => (
                    <div className="gallery-container-draggable" ref={provided.innerRef} {...provided.draggableProps}>
                        <div key={mediaKey} className="gallery-container">
                            <div
                                className={`gallery-item ${!showPseudoHover ? 'show-overlay' : ''} ${
                                    currentIndex === index && highlightSelected ? 'highlight' : ''
                                } ${snapshot.isDragging ? 'display-handler' : ''}`}
                                style={{
                                    height: canEditImage && mediaFormat === MediaType.Image ? '145px' : height,
                                    width,
                                }}
                                role="button"
                                aria-label="View Image"
                                onClick={(): void => {
                                    if (openModalOnMediaClick && onMediaClick) {
                                        onMediaClick(index);
                                    }
                                }}
                                onKeyPress={(): void => {
                                    // Do Nothing
                                }}
                                tabIndex={0}
                            >
                                {renderer}
                                <div
                                    className={`${
                                        showPseudoHover || openModalOnMediaClick ? 'gallery-item-hover' : ''
                                    } ${currentIndex === index && highlightSelected ? 'highlight' : ''} ${
                                        canEditImage && mediaFormat === MediaType.Image ? 'response-editor-img' : ''
                                    }`}
                                >
                                    {canReOrder || displayFileNameOnHover || openModalOnHandleIconClick || canDelete ? (
                                        <section className="gallery-handler">
                                            <article className="stack-left" {...provided.dragHandleProps}>
                                                {canReOrder ? (
                                                    <HandlerIcon
                                                        className="move-media"
                                                        tooltip="MOVE"
                                                        handleClick={null}
                                                        mediaIndex={index}
                                                        iconName="icon-arrows-alt"
                                                    />
                                                ) : null}
                                            </article>
                                            <article className="stack-right">
                                                {displayFileNameOnHover ? (
                                                    <HandlerIcon
                                                        className="info-media"
                                                        tooltip={media.name}
                                                        handleClick={null}
                                                        mediaIndex={index}
                                                        iconName="icon-info-circle-circle"
                                                    />
                                                ) : null}
                                                {openModalOnHandleIconClick ? (
                                                    <HandlerIcon
                                                        className="open-media"
                                                        tooltip="OPEN"
                                                        handleClick={onMediaClick}
                                                        mediaIndex={index}
                                                        iconName="icon-external-link-alt"
                                                    />
                                                ) : null}
                                                {canDelete ? (
                                                    <HandlerIcon
                                                        className="delete-media"
                                                        tooltip="REMOVE"
                                                        handleClick={onDelete}
                                                        mediaIndex={index}
                                                        iconName="icon-times"
                                                    />
                                                ) : null}
                                            </article>
                                        </section>
                                    ) : null}
                                    {maxCount && index === maxCount - 1 && mediaData.length - maxCount ? (
                                        <span>{`${mediaData.length - maxCount}+`}</span>
                                    ) : null}

                                    {!canEditImage &&
                                        openModalOnMediaClick &&
                                        onMediaClick &&
                                        mediaPathsToAltText &&
                                        mediaPathsToAltText[(galleryData[index] as UploadedFileAsset).url] && (
                                            <span className="gallery-item-alt-text">
                                                {mediaPathsToAltText[(galleryData[index] as UploadedFileAsset).url]}
                                            </span>
                                        )}
                                </div>
                                {canEditImage && mediaFormat === MediaType.Image && (
                                    <div className="gallery-item-actions">
                                        <span
                                            className="gallery-item-action"
                                            onClick={() => onMediaEditClick(index)}
                                            onKeyDown={() => {
                                                // do nothing
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            {t('EDIT')}
                                        </span>
                                        {selectedSources?.FACEBOOK && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip id="alt-text-tooltip">
                                                        {t('ALT_TEXT_FACEBOOK_ONLY')}
                                                    </Tooltip>
                                                }
                                            >
                                                <span
                                                    className="gallery-item-action"
                                                    onClick={() =>
                                                        onMediaAltTextClick((mediaData[index] as UploadedFileAsset).url)
                                                    }
                                                    onKeyDown={() => {
                                                        // do nothing
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    {t('ALT_TEXT')}
                                                </span>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    };

    const renderMediaList = (galleryItem: File | UploadedFileAsset, galleryKey: number): JSX.Element => {
        return (
            <div key={galleryKey} className="media-preview-off">
                <span className="media-file-name">{galleryItem.name}</span>
                {canDelete ? (
                    <OverlayTrigger overlay={<Tooltip id="handler-tooltip-delete">{t('REMOVE')}</Tooltip>}>
                        <span
                            role="button"
                            onClick={(): void => {
                                if (onDelete) {
                                    onDelete(galleryKey);
                                }
                            }}
                            onKeyPress={(): void => {
                                // Do Nothing
                            }}
                            tabIndex={0}
                        >
                            <i className="icon-times" />
                        </span>
                    </OverlayTrigger>
                ) : null}
            </div>
        );
    };

    return (
        <div className="r4e-gallery-preview">
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="gallery-preview-droppable" direction="horizontal">
                    {(provided): JSX.Element => (
                        <div
                            className="gallery-preview-droppable-container"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            <div
                                ref={containerRef}
                                className="gallery-container gallery-preview-draggable-container"
                                style={{
                                    flexWrap: wrapContent ? 'wrap' : 'nowrap',
                                    overflowX: overflowXContent ? 'auto' : 'visible',
                                    paddingTop:
                                        canReOrder || displayFileNameOnHover || openModalOnHandleIconClick || canDelete
                                            ? '12px'
                                            : '',
                                }}
                            >
                                {mediaData.map((galleryItem, galleryKey: number) => {
                                    if (displayPreview) {
                                        if (maxCount) {
                                            if (galleryKey < maxCount) {
                                                return renderMediaPreview(galleryItem, galleryKey);
                                            }
                                        } else {
                                            return renderMediaPreview(galleryItem, galleryKey);
                                        }
                                    } else {
                                        return renderMediaList(galleryItem, galleryKey);
                                    }
                                    return false;
                                })}
                                {provided.placeholder}
                            </div>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

GalleryPreview.defaultProps = {
    displayPreview: true,
    height: '95px',
    width: '125px',
    wrapContent: true,
    overflowXContent: false,
    canDelete: true,
    displayFileNameOnHover: true,
    openModalOnMediaClick: false,
    openModalOnHandleIconClick: true,
    maxCount: null,
    highlightSelected: false,
    showVideoControls: true,
    showAudioControls: true,
    canReOrder: false,
    canEditImage: false,
    scrollSelectedMediaInToView: false,
};
